import { FieldWidthEnum } from '../../../cer/cer-data/cer-data-types';
import { UiCommand, UiKeyboardShortcut, UiCommandEvent } from '@ui/cer-ui-types';
import { CerUiIcons } from '@ui/cer-ui-icons';

import { BankAccountSourceSyncCommand, BankSettlementStatusEnum, BankTransactionBatchStatusSetCommand, BankTransactionSourceBatchClient } from 'src/app/api';
import { CerFormAdapter } from '@cer/cer-form/cer-form-adapter';
import { CerFormViewSet } from '@cer/cer-form/cer-form-view-set';
import { CerFormView, ICerViewComponent } from '@cer/cer-form/cer-form-view';
import { CerFormPanesSettingsUtil } from '@cer/cer-form/cer-form-panes/cer-form-panes.service';

export class BankAccountTransactionBatchForm extends CerFormAdapter {

  private uiCmdSyncBatch: UiCommand = { id: 'SyncBatch', text: 'Synkroniser kilder', tooltipText: 'Synkroniser transaktioner fra alle kilder (ALT+S)', iconCss: CerUiIcons.research, target: ".e-content", needSelection: false };
  private uiCmdSetOpen: UiCommand = { id: 'SetOpen', text: 'Åbn', tooltipText: 'Åbn transaktioner for den markerede (ALT+O)', iconCss: CerUiIcons.play, target: ".e-content", needSelection: true };
  private uiCmdSetDraft: UiCommand = { id: 'SetDraft', text: 'Kladde', tooltipText: 'Transaktioner har status Kladde for den markerede (ALT+K)', iconCss: CerUiIcons.pause, target: ".e-content", needSelection: true };
  private uiCmdSyncBatchShortcut: UiKeyboardShortcut = { cmd: this.uiCmdSyncBatch, code: 'KeyS', shift: false, ctrl: false, alt: true };
  private uiCmdSetOpenShortcut: UiKeyboardShortcut = { cmd: this.uiCmdSetOpen, code: 'KeyO', shift: false, ctrl: false, alt: true };
  private uiCmdSetDraftShortcut: UiKeyboardShortcut = { cmd: this.uiCmdSetDraft, code: 'KeyK', shift: false, ctrl: false, alt: true };

  public override viewSetInit(viewSet: CerFormViewSet): void {
    viewSet.panes = CerFormPanesSettingsUtil.masterDetail('horizontal', true, 60);

    const view = new CerFormView({
      viewMetadata: {
        name: 'bank-transaktion-indlæsning', dataApiName: 'BankAccountTransactionSourceBatch', text: 'Indlæsninger',
        //   dataApiParams: ['$sourceGroupIdFilter=2'],
        primaryKey: 'id', allowEdit: false, allowCreate: false, allowDelete: true, baseFields: ['id', 'history']
      },
      fieldMetadata:
        [
          { name: 'batchDate', text: 'Dato', format: 'DateShort', orderBy: 'Descending', orderByPriority: 1 },
          { name: 'bankAccount', text: 'Bankkonto', foreignKeyTableName: "BankAccount", widthType: FieldWidthEnum.XXS, orderBy: 'Ascending', orderByPriority: 2 },
          { name: 'source', text: 'Kilde', foreignKeyTableName: "BankAccountTransactionSource", widthType: FieldWidthEnum.XXS, filterValue: 2, filterOperator: 'equal', filterOnClient: true },
          { name: 'sourceId', text: 'Dokument', format: 'Integer', widthType: FieldWidthEnum.XXS, visible: false },
          { name: 'name', text: 'Navn', visible: false },
          { name: 'startBalance', text: 'Startsaldo', format: 'Amount' },
          { name: 'endBalance', text: 'Slutsaldo', format: 'Amount' },
          { name: 'status', text: 'Status', foreignKeyTableName: "BankAccountTransactionStatus", widthType: FieldWidthEnum.XXS }
        ],
      toolbarCommands: [this.uiCmdSyncBatch, this.uiCmdSetOpen, this.uiCmdSetDraft],
      contextMenuCommands: [this.uiCmdSetOpen, this.uiCmdSetDraft],
      keyboardShortcuts: [this.uiCmdSyncBatchShortcut, this.uiCmdSetOpenShortcut, this.uiCmdSetDraftShortcut]
    });
    view.onCommandEvent = (e => this.onCommand(e));    

    viewSet.viewAdd(view);

    const viewTrans = new CerFormView({
      viewMetadata: {
        name: 'bank-transaktion', dataApiName: 'BankAccountTransaction', text: 'Poster',
        primaryKey: 'id', parentPrimaryKey: 'id', parentForeignKey: 'sourceBatch', baseFields: ['id', 'history']
      },
      fieldMetadata: [
        { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for posten', visible: false, orderBy: 'Descending' },
        { name: 'sourceBatch', text: 'Batch', format: 'Integer', visible: false },
        { name: 'bankAccount', text: 'Bankkonto', foreignKeyTableName: "BankAccount", widthType: FieldWidthEnum.S, visible: false },
        { name: 'source', text: 'Kilde', foreignKeyTableName: "BankAccountTransactionSource", visible: false },
        { name: 'bankTransId', text: 'Bank trans id', widthType: FieldWidthEnum.XS, visible: false },
        { name: 'bankTransType', text: 'Bank trans type', widthType: FieldWidthEnum.XS, visible: false },
        { name: 'transDate', text: 'Dato', format: 'DateShort', widthType: FieldWidthEnum.S, orderBy: 'Descending' },
        { name: 'txt', text: 'Tekst', widthType: FieldWidthEnum.XXL },
        { name: 'authReference', text: "Nota Indløser", widthType: FieldWidthEnum.XXS },
        { name: 'bankCreditCard', text: "Kreditkort", widthType: FieldWidthEnum.XS, foreignKeyTableName: 'BankCreditCard', visible: false },
        { name: 'user', text: 'Medarbejder', widthType: FieldWidthEnum.XXS, foreignKeyTableName: 'User', foreignKeyValue: 'shortName' },
        { name: 'amount', text: 'Beløb', format: 'Amount', widthType: FieldWidthEnum.L, footerType: 'Sum' },
        { name: 'balance', text: 'Saldo', format: 'Amount', widthType: FieldWidthEnum.L },
        { fieldType: 'currencyId' },
        { name: 'status', text: 'Status', foreignKeyTableName: "BankAccountTransactionStatus", widthType: FieldWidthEnum.XXS }
      ]
    });
    viewSet.viewAdd(viewTrans,view);
  }

  public onCommand(event: UiCommandEvent) {
    switch (event.commandId) {
      case this.uiCmdSyncBatch.id:
        this.syncBatch(event.appComponent);
        break;
      case this.uiCmdSetOpen.id:
        this.setStatus(event, BankSettlementStatusEnum.Open);
        break;
      case this.uiCmdSetDraft.id:
        this.setStatus(event, BankSettlementStatusEnum.Draft);
        break;
    }
  }

  private syncBatch(appComponent: ICerViewComponent) {
    var cmd = new BankAccountSourceSyncCommand({ syncGenericSources: true });
    this.syncBatchApi(cmd, appComponent);
  }

  private syncBatchApi(cmd: BankAccountSourceSyncCommand, appComponent: ICerViewComponent) {
    const ui = this.context.ui;
    ui.operationBegin('Synkroniserer kilder');
    //var = new BankTransactionSourceBatchClient());
    var batchClient: BankTransactionSourceBatchClient = this.context.injector.get(BankTransactionSourceBatchClient);
    batchClient.syncBatch(cmd).subscribe({
      next: (result) => {
        ui.operationEnd();
        ui.toast(result + ' bevægelser hentet');
        appComponent.research();
      },
      error: (error) => {
        ui.operationEndError(error);
      }
    });
  }

  private setStatus(event: UiCommandEvent, status: BankSettlementStatusEnum) {
    const appComponent: ICerViewComponent = event.appComponent;
    var rowData: any = event.args?.rowData;
    if (rowData == null) {
      rowData = event.args?.rowInfo?.rowData;
    }
    if (rowData == null) {
      rowData = appComponent.rowSelectedData();
    }
    var id: number = rowData?.id;
    if (id != null) {
      var idList: number[] = [id];
      var cmd = new BankTransactionBatchStatusSetCommand({ batchIdList: idList, status: status });
      this.setStatusApi(cmd, appComponent);
    }
  }

  private setStatusApi(cmd: BankTransactionBatchStatusSetCommand, appComponent: ICerViewComponent) {
    const ui = this.context.ui;
    ui.operationBegin();
    var batchClient: BankTransactionSourceBatchClient = this.context.injector.get(BankTransactionSourceBatchClient);
    batchClient.setStatus(cmd).subscribe({
      next: (count) => {
        ui.operationEnd();
        if (count > 0) {
          appComponent.research();
        }
        else {
          ui.toast('Ingen indlæsning er opdateret');
        }
      },
      error: (error) => {
        ui.operationEndError(error);
      }
    });
  }

}
